import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useTable, useSortBy, useFilters } from "react-table";
import { requireToken, SessionExpiredError } from "../auth/authService";
import { configs } from "../configurations";
import { ColumnFilter } from "./ColumnFilter";
import { CopyableId } from "./CopyableId";
import {
  getLatestMerchantReport,
  startMerchantReport,
} from "./merchantConfigurationApi";
import { downloadMerchantReport } from "./merchantReportExport";
import { PageHeader } from "./PageHeader";

//  A full report takes a minute or two, so a few seconds between checks is plenty.
const POLL_INTERVAL_MS = 3000;

const RUNNING = "Running";
const COMPLETED = "Completed";
const FAILED = "Failed";

function FormattedDate({ value }) {
  if (!value) return null;

  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return <span title={at.toLocaleString()}>{at.toLocaleDateString()}</span>;
}

function YesNo({ value }) {
  if (value === null || value === undefined) return null;
  return value ? "Yes" : "No";
}

//  ISO strings sort correctly as strings. react-table's default sort turns a missing date
//  into the string "undefined", which would sort it among the real ones.
function sortByIsoDate(rowA, rowB, columnId) {
  const a = rowA.values[columnId] || "";
  const b = rowB.values[columnId] || "";
  return a === b ? 0 : a > b ? 1 : -1;
}

function sortByFlag(rowA, rowB, columnId) {
  const rank = (value) => (value === true ? 2 : value === false ? 1 : 0);
  return rank(rowA.values[columnId]) - rank(rowB.values[columnId]);
}

const dateColumn = (header, accessor) => ({
  header,
  accessor,
  Cell: ({ value }) => <FormattedDate value={value} />,
  sortType: sortByIsoDate,
  disableFilters: true,
});

const flagColumn = (header, accessor) => ({
  header,
  accessor,
  Cell: ({ value }) => <YesNo value={value} />,
  sortType: sortByFlag,
  disableFilters: true,
});

function ReportTable({ rows: data }) {
  const columns = useMemo(
    () => [
      { header: "Account Name", accessor: "accountName" },
      {
        header: "Stripe Account ID",
        accessor: "accountId",
        Cell: ({ value }) =>
          value ? <CopyableId value={value} label="Stripe account ID" /> : null,
      },
      { header: "Email", accessor: "accountEmail" },
      { header: "Partner", accessor: "partnerName" },
      { header: "TMS", accessor: "tms" },
      dateColumn("Created", "createdDate"),
      dateColumn("ToS Accepted", "tosAcceptedDate"),
      dateColumn("Last Transaction", "lastTransactionDate"),
      flagColumn("Enabled", "isEnabled"),
      flagColumn("Payments", "paymentsEnabled"),
      flagColumn("Payouts", "payoutsEnabled"),
      flagColumn("IC++", "icPlusPlus"),
      { header: "Effective Pricing", accessor: "effectivePricing" },
      { header: "Notes", accessor: "notes" },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: ColumnFilter }), []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn,
        initialState: { sortBy: [{ id: "createdDate", desc: true }] },
      },
      useFilters,
      useSortBy
    );

  return (
    <div className="table-responsive">
      <table
        {...getTableProps()}
        className="table table-striped text-nowrap align-middle"
        aria-labelledby="merchantReportLabel"
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                //  align-top: date and flag columns have no filter box, and the table's
                //  align-middle would drop their labels below the others.
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="align-top"
                >
                  <div className="text-nowrap">
                    {column.render("header")}
                    <i
                      className={`bi ms-1 ${
                        column.isSorted
                          ? column.isSortedDesc
                            ? "bi-arrow-down"
                            : "bi-arrow-up"
                          : "bi-arrow-up invisible"
                      }`}
                      aria-hidden="true"
                    />
                  </div>
                  {/*  stopPropagation for the same reason as Manage Merchants: the sort
                       toggle sits on the <th>, so a click into the filter would re-sort. */}
                  <div className="mt-2" onClick={(event) => event.stopPropagation()}>
                    {column.canFilter ? column.render("Filter") : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReportStatus({ report }) {
  if (!report) {
    return <p className="text-secondary">No report has been generated yet.</p>;
  }

  if (report.status === RUNNING) {
    return (
      <p className="d-flex align-items-center" role="status">
        <span className="spinner-border spinner-border-sm text-primary me-2" aria-hidden="true" />
        {report.total > 0
          ? `Generating: ${report.processed} of ${report.total} merchants checked in Stripe.`
          : "Generating: loading merchants."}
      </p>
    );
  }

  if (report.status === FAILED) {
    return (
      <div className="alert alert-danger" role="alert">
        The last report failed: {report.error || "unknown error"}
      </div>
    );
  }

  const at = report.completedUtc ? new Date(report.completedUtc) : null;
  return (
    <p className="text-secondary">
      Generated {at ? at.toLocaleString() : ""}
      {report.requestedBy ? ` by ${report.requestedBy}` : ""}.{" "}
      {report.rows.length} merchants.
    </p>
  );
}

/**
 * The TowPay admin merchant report: every merchant with its partner, TMS, Stripe status,
 * IC++ flag and the pricing its recent charges show, downloadable as .xlsx.
 *
 * The API builds the report in the background, so Generate starts a run and the page
 * polls until it is done.
 *
 * @param {object} props
 * @param {number} [props.pollIntervalMs] how often to check a running report; tests shorten it
 */
export function MerchantReport({ pollIntervalMs = POLL_INTERVAL_MS }) {
  const [baseUri, setBaseUri] = useState(null);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  //  The poll and the config load both resolve after the user may have left the page.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    configs().then((config) => {
      if (mounted.current) setBaseUri(config.merchantConfigurationBaseUri || null);
    });
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const latest = await getLatestMerchantReport(requireToken(), baseUri);
      if (!mounted.current) return;
      setReport(latest);
      setError(null);
    } catch (err) {
      //  requireToken is already navigating to the login screen.
      if (err instanceof SessionExpiredError) return;
      if (mounted.current) setError(err.message || "Failed to load the merchant report.");
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [baseUri]);

  useEffect(() => {
    if (baseUri) loadLatest();
  }, [baseUri, loadLatest]);

  const isRunning = report?.status === RUNNING;

  useEffect(() => {
    if (!isRunning || !baseUri) return undefined;

    const timer = setInterval(loadLatest, pollIntervalMs);
    return () => clearInterval(timer);
  }, [isRunning, baseUri, loadLatest, pollIntervalMs]);

  const handleGenerate = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const run = await startMerchantReport(requireToken(), baseUri);
      if (mounted.current) setReport(run);
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      if (mounted.current) setError(err.message || "Failed to start the merchant report.");
    } finally {
      if (mounted.current) setIsStarting(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadMerchantReport(report.rows, report.completedUtc);
    } catch (err) {
      console.error("Error building the merchant report spreadsheet:", err);
      if (mounted.current) setError("Could not build the spreadsheet. Please try again.");
    } finally {
      if (mounted.current) setIsDownloading(false);
    }
  };

  const completedRows =
    report?.status === COMPLETED && Array.isArray(report.rows) ? report.rows : null;

  return (
    <div>
      <PageHeader
        title="Merchant Report"
        titleId="merchantReportLabel"
        lead="Every TowPay merchant with its partner, TMS, Stripe status, IC++ and the pricing its last 100 charges show."
      />

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={!baseUri || isLoading || isStarting || isRunning}
        >
          {isRunning || isStarting ? "Generating..." : "Generate report"}
        </Button>
        <Button
          variant="outline-primary"
          onClick={handleDownload}
          disabled={!completedRows || completedRows.length === 0 || isDownloading}
        >
          <i className="bi bi-download me-1" aria-hidden="true" />
          {isDownloading ? "Preparing..." : "Download .xlsx"}
        </Button>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <ReportStatus report={report} />
          {completedRows && completedRows.length > 0 ? (
            <ReportTable rows={completedRows} />
          ) : null}
        </>
      )}
    </div>
  );
}

export default MerchantReport;
